import UserAvatar from "@/components/lazo-ai-chat-components/Avatar";
import CommonButton from "@/components/utils/CommonButton";
import { LazoLogoPurpleNoLetters } from "@/components/utils/LazoLogo";
import LazoModal from "@/components/utils/LazoModal";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { SmallUserDTO } from "@/models/SmallUserDTO";
import LazoUserService from "@/services/LazoUserService";
import { ExternalId } from "@/types/ExternalId";
import { Box, MenuItem, MenuList, SxProps, Typography, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  onConfirm: (members: string[]) => Promise<ApiResponseV2<any>>;
  minSelected: number;
  toFilter?: string[];
};

export function OneGroupChatModal(props: Props) {
  const { t } = useTranslation("chat");
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedUsers, setSelectedUsers] = useState<{ [k: ExternalId]: ExternalId }>({});
  const [suggestions, setSuggestions] = useState<SmallUserDTO[]>([]);
  const [processing, setProcessing] = useState<boolean>(false);

  const currentUserExternalId = AuthService.getUser().externalId;
  const tenantMembership = useTenantMemberships().currentTenantMembership;

  useEffect(() => {
    if (!props.open) return;
    LazoUserService.getChatUserSuggestions(null, tenantMembership.tenant.externalId, null)
      .then((r) => {
        r.onSuccess((c) =>
          setSuggestions(
            c.filter((sug) => sug.externalId !== currentUserExternalId && (!!!props.toFilter || !props.toFilter.includes(sug.externalId)))
          )
        );
        r.notifyErrorIfPresent();
      })
      .finally(() => setLoading(false));
  }, [props.open]);

  function onClickSuggestion(entry: SmallUserDTO) {
    if (selectedUsers[entry.externalId]) {
      setSelectedUsers((prev) => {
        const copy = { ...prev };
        copy[entry.externalId] = undefined;
        return copy;
      });
    } else {
      setSelectedUsers((prev) => {
        const copy = { ...prev };
        copy[entry.externalId] = entry.externalId;
        return copy;
      });
    }
  }

  function onConfirmAction() {
    setProcessing(true);
    props
      .onConfirm(Object.values(selectedUsers).filter((su) => su !== undefined))
      .then((r) => {
        r.onSuccess(() => {
          setSelectedUsers({});
          props.onClose();
        });
      })
      .finally(() => setProcessing(false));
  }

  return (
    <LazoModal
      open={props.open}
      handleClose={props.onClose}
      title={props.title}
      sx={ModalContentSx}
      buttons={
        !loading ? (
          <ConfirmButton
            onClick={onConfirmAction}
            disabled={processing || loading || Object.values(selectedUsers).filter((v) => v !== undefined).length < props.minSelected}
          >
            {processing ? <Spinner size={"1rem"} /> : <Typography>{t("groupChatModal.confirmButton")}</Typography>}
          </ConfirmButton>
        ) : undefined
      }
    >
      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="200px">
          <Spinner size={"2rem"} sx={{ color: Colors.LAZO_PURPLE }} />
        </Box>
      ) : (
        <>
          <SuggestionsContainer>
            <SuggestionsMenuList>
              {suggestions.map((entry) => (
                <SuggestionItemContainer
                  key={entry.externalId}
                  onClick={() => onClickSuggestion(entry)}
                  selected={!!selectedUsers[entry.externalId]}
                >
                  <SuggestionAvatarContainer>
                    <UserAvatar sx={SuggestionUserAvatarSx} user={{ firstName: entry.firstName, lastName: entry.lastName } as User} />
                    <Box display="flex" flexDirection="column" justifyContent="center">
                      <Typography>{entry.firstName + " " + entry.lastName}</Typography>
                      <Typography fontSize="12px" sx={{ color: Colors.DARKER_DARKER_GRAY }}>
                        {entry.email}
                      </Typography>
                    </Box>
                  </SuggestionAvatarContainer>
                  {entry.isLazoEmployee && <LazoLogoPurpleNoLetters sxClass={{ width: "10px" }} />}
                </SuggestionItemContainer>
              ))}
            </SuggestionsMenuList>
            {suggestions.length === 0 && (
              <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                <Typography sx={{ color: Colors.DARKER_DARKER_GRAY }}>{t("groupChatModal.noSuggestionsMessage")}</Typography>
              </Box>
            )}
          </SuggestionsContainer>
        </>
      )}
    </LazoModal>
  );
}

const ModalContentSx: SxProps = {
  width: { xs: "95vw", sm: "85vw", md: "500px", lg: "600px" },
  maxHeight: { xs: "85vh", sm: "80vh", md: "70vh" },
};

const SuggestionsContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: "200px",
  maxHeight: "400px",
  overflowY: "auto",
}));

const SuggestionItemContainer = styled(MenuItem, { shouldForwardProp: (prop) => prop !== "selected" })<{ selected: boolean }>(
  ({ selected }) => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    transition: "all 0.125s ease-out",
    backgroundColor: selected ? Colors.BUTTON_SECONDARY_PURPLE : "transparent",
    ...(selected && {
      ":hover": {
        backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
      },
    }),
  })
);

const SuggestionAvatarContainer = styled(Box)(() => ({
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
}));

const SuggestionUserAvatarSx: SxProps = {
  width: "36px",
  height: "36px",
  minWidth: "36px",
  minHeight: "36px",
  borderRadius: "0.125rem",
  backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
  backgroundImage: "none",
  border: `1px solid ${Colors.DARKER_DARKER_GRAY}`,
  color: Colors.ELEGANT_BLACK,
  fontWeight: 400,
  fontSize: "1rem",
};

const SuggestionsMenuList = styled(MenuList)(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  flex: 1,
}));

const ConfirmButton = styled(CommonButton)(() => ({
  width: "100%",
  transition: "all 0.125s ease-out",
}));
